import { Dispatch, SetStateAction, useEffect } from 'react';
import { fetchQuery } from 'relay-runtime';
import environment from 'src/relay/environment';
import { aiPresetsQuery } from '../queries/__generated__/aiPresetsQuery.graphql';
import { AIPresetsQuery } from '../queries/aiPresets';
import { SelectionValue } from '../components/presets/presetOptionButtons';
import { getPresetTypesForTool } from '../components/presets/presetTypeConfig';
import { ToolType } from '../types';
import { AIStudioState } from './useAIStudioState';

/**
 * Fetch default selections for a tool based on preset type configs.
 * Uses fetchQuery to get actual preset data including thumbnails.
 */
async function fetchDefaultSelectionsForTool(toolId: ToolType): Promise<Record<string, SelectionValue>> {
    const presetTypes = getPresetTypesForTool(toolId);
    const selections: Record<string, SelectionValue> = {};

    const typesWithDefaults = presetTypes.filter((t) => t.defaultPresetId);

    if (typesWithDefaults.length === 0) {
        return selections;
    }

    const fetchPromises = typesWithDefaults.map(async (typeConfig) => {
        try {
            const data = await fetchQuery<aiPresetsQuery>(environment, AIPresetsQuery, {
                toolId: typeConfig.categoryToolId || toolId,
                presetType: typeConfig.presetType
            }).toPromise();

            if (data?.aiPresets) {
                const preset = data.aiPresets.find((p) => p.id === typeConfig.defaultPresetId);
                if (preset) {
                    return {
                        typeId: typeConfig.id,
                        selection: {
                            id: preset.id,
                            thumbnailUrl: preset.previewUrl || preset.imageUrl || undefined,
                            factor: preset.factor ?? undefined
                        }
                    };
                }
            }
        } catch (error) {
            console.error(`Failed to fetch default preset for ${typeConfig.id}:`, error);
        }
        return null;
    });

    const results = await Promise.all(fetchPromises);

    for (const result of results) {
        if (result) {
            selections[result.typeId] = result.selection;
        }
    }

    return selections;
}

/**
 * Hook that fetches and merges default preset selections when the selected tool changes.
 */
export function useDefaultSelections(selectedTool: ToolType | null, setState: Dispatch<SetStateAction<AIStudioState>>): void {
    useEffect(() => {
        if (!selectedTool) return;
        let cancelled = false;

        fetchDefaultSelectionsForTool(selectedTool).then((defaultSelections) => {
            if (cancelled) return;

            setState((prev) => {
                if (prev.selectedTool !== selectedTool) return prev;

                const mergedSelections = { ...prev.selections };
                for (const [typeId, selection] of Object.entries(defaultSelections)) {
                    if (!mergedSelections[typeId]) {
                        mergedSelections[typeId] = selection;
                    }
                }

                return {
                    ...prev,
                    selections: mergedSelections
                };
            });
        });

        return () => {
            cancelled = true;
        };
    }, [selectedTool, setState]);
}
